import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PropTypes } from "prop-types";
import React from "react";
import { withRouter } from "react-router-dom";
import DocumentActions from "~/actions/document_actions";
import NotificationButton from "~/components/Notifications/Button";
import DocumentUploader from "~/components/navigation/document_uploader";
import DocumentLimitAlertChecker from "~/components/shared/document_limit_alert_checker";
import JobLimitAlertChecker from "~/components/shared/job_limit_alert_checker";
import DrawerContext from "~/contexts/DrawerContext";
import { captureEvent } from "~/helpers/capture_posthog_event";
import { getFeatureFlag } from "~/helpers/FeatureFlagChecker";
import { checkIfJobLimitExceeded } from "~/helpers/job_helpers";
import HelpCenter from "./help_center";
import NavBarSupportedContent from "./nav_bar_supported_content";

class RecruitingNavContent extends React.PureComponent {
  static contextType = DrawerContext;
  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      showLimitAlert: false,
      showJobLimitAlert: false,
    };

    this.documentUploaderRef = React.createRef();
  }

  handleUploadComplete = () => {
    DocumentActions.loadDocuments();
  };

  handleDocumentUploadClick = () => {
    this.documentUploaderRef.current.handleFileSelect();
  };

  setShowLimitAlert = (showLimitAlert) => {
    this.setState({ showLimitAlert });
  };

  setShowJobLimitAlert = (showJobLimitAlert) => {
    this.setState({ showJobLimitAlert });
  };

  handleLoadingStateChange = (isLoading) => {
    this.setState({ isLoading });
  };

  onNewLeadClick = (e) => {
    e.preventDefault();
    const { openDrawer } = this.context;

    openDrawer("new-lead");
  };

  onAddVideoClick = async (e) => {
    e.preventDefault();

    // Lazy load VideosModal to avoid loading video.js (1.7 MB) on every page
    const { mountVideosModal } = await import(
      /* webpackChunkName: "videos-modal" */ "~/components/modals/videos/VideosModal"
    );

    mountVideosModal({
      modalClass: "modal modal-overlay",
      dialogClass: "modal-dialog modal-lg",
      skipVideoLibrary: true,
    });

    captureEvent("video_page_add_video");
  };

  onAddJobClick = async (e) => {
    e.preventDefault();

    const isLimitExceeded = await checkIfJobLimitExceeded();
    if (isLimitExceeded === null) {
      return;
    }

    if (isLimitExceeded) {
      this.setShowJobLimitAlert(true);
    } else {
      const { history } = this.props;
      history.push("/recruiter/jobs/new");
      captureEvent("created_job_page");
    }
  };

  onAddJobCandidateClick = (e) => {
    e.preventDefault();
    const { openDrawer } = this.context;
    const { match } = this.props;
    if (match?.params?.id) {
      openDrawer("new-lead", {
        jobId: parseInt(match.params.id, 10),
      });
      captureEvent("add_candidate");
    }
  };

  renderActionButton() {
    const { location } = this.props;
    const { isLoading, showLimitAlert, showJobLimitAlert } = this.state;
    const isDocumentPage =
      location.pathname === "/recruiter/documents" ||
      location.pathname === "/recruiter/documents/trash";
    const isVideoPage =
      location.pathname === "/recruiter/videos" ||
      location.pathname === "/recruiter/videos/trash";
    const isJobCandidatesPage = location.pathname.includes("/candidates");
    const isJobPage =
      !isJobCandidatesPage && location.pathname.includes("/recruiter/jobs");
    const videoManagementVariant = getFeatureFlag(
      process.env.SHAKAPACKER_PUBLIC_ENABLE_VIDEO_MANAGEMENT_FLAG,
    );
    const isVideoManagementEnabled = ["displayed"].includes(
      videoManagementVariant,
    );
    const jobsFeatureVariant = getFeatureFlag(
      process.env.SHAKAPACKER_PUBLIC_ENABLE_JOBS_ENTITY_FLAG,
    );
    const isJobsFeatureEnabled = jobsFeatureVariant === "displayed";

    if (isDocumentPage) {
      return (
        <DocumentLimitAlertChecker
          showLimitAlert={showLimitAlert}
          setShowLimitAlert={this.setShowLimitAlert}
          placement="bottom"
        >
          <li className="nav-item mr20-md-up">
            <a
              className={`nav-link btn btn-success text-white ${isLoading ? "disabled" : ""}`}
              href="#"
              onClick={this.handleDocumentUploadClick}
              disabled={isLoading}
            >
              <FontAwesomeIcon
                icon={isLoading ? "spinner" : ["far", "fa-plus"]}
                spin={isLoading}
              />{" "}
              Add Document
            </a>
            <DocumentUploader
              ref={this.documentUploaderRef}
              onUploadComplete={this.handleUploadComplete}
              onLoadingStateChange={this.handleLoadingStateChange}
              setShowLimitAlert={this.setShowLimitAlert}
            />
          </li>
        </DocumentLimitAlertChecker>
      );
    }

    if (isVideoPage) {
      if (!isVideoManagementEnabled) {
        return null;
      }
      return (
        <li className="nav-item mr20-md-up">
          <a
            className="nav-link btn btn-success text-white"
            href="#add-video"
            onClick={this.onAddVideoClick}
          >
            <FontAwesomeIcon icon={["far", "fa-plus"]} /> Add Video
          </a>
        </li>
      );
    }

    if (isJobPage) {
      if (!isJobsFeatureEnabled || !Rails.abilities.manageJob) {
        return null;
      }
      return (
        <JobLimitAlertChecker
          showJobLimitAlert={showJobLimitAlert}
          setShowJobLimitAlert={this.setShowJobLimitAlert}
          placement="bottom"
          title="Oh no, you've reached your allotted jobs limit"
          message="Delete jobs or upgrade your subscription"
        >
          <li className="nav-item mr20-md-up">
            <a
              className="nav-link btn btn-success text-white"
              href="/recruiter/jobs/new"
              onClick={this.onAddJobClick}
            >
              <FontAwesomeIcon icon={["far", "fa-plus"]} /> Add Job
            </a>
          </li>
        </JobLimitAlertChecker>
      );
    }

    if (isJobCandidatesPage) {
      if (!isJobsFeatureEnabled) {
        return null;
      }
      return (
        <li className="nav-item mr20-md-up">
          <a
            className="nav-link btn btn-success text-white"
            href="#add-job-candidate"
            onClick={this.onAddJobCandidateClick}
          >
            <FontAwesomeIcon icon={["far", "fa-plus"]} /> Add Candidate
          </a>
        </li>
      );
    }

    return (
      <li className="nav-item mr20-md-up">
        <a
          className="nav-link btn btn-success text-white"
          href="#new-lead-drawer"
          onClick={this.onNewLeadClick}
        >
          <FontAwesomeIcon icon={["far", "fa-plus"]} /> Add Lead
        </a>
      </li>
    );
  }

  render() {
    return (
      <ul className="navbar-nav ml-auto mr-auto-sm-down">
        <div className="d-flex">
          <NotificationButton />
          <HelpCenter />
        </div>

        {this.renderActionButton()}
        <NavBarSupportedContent />
      </ul>
    );
  }
}

RecruitingNavContent.propTypes = {
  location: PropTypes.object.isRequired,
  history: PropTypes.shape({}),
};

export default withRouter(RecruitingNavContent);
